import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type ComedyChannel } from '../../lib/api';
import { Button, ErrorAlert, PageHeader, Spinner } from '../../components/ui';
import { COMEDY_TABS, ComedyEmpty, type ComedyTabId } from './ComedyShared';
import { ComedyDashboardPanel } from './ComedyDashboardPanel';
import { ComedyHelpPanel } from './ComedyHelpPanel';
import { ComedyChannelsPanel } from './ComedyChannelsPanel';
import { ComedyLibraryPanel, ComedyResearchPanel, ComedySourcesPanel } from './ComedyResearchPanels';
import { ComedyDirectorPanel, ComedyScenariosPanel } from './ComedyCreativePanels';
import {
  ComedyAnalyticsPanel, ComedyPublicationsPanel, ComedyQueuePanel, ComedySchedulePanel, ComedySettingsPanel, ComedyVideoStudioPanel,
} from './ComedyOpsPanels';
import '../../styles/comedy.css';

export function ComedyStudioPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const tabParam = params.get('section') as ComedyTabId | null;
  const tab: ComedyTabId = COMEDY_TABS.some((item) => item.id === tabParam) ? (tabParam as ComedyTabId) : 'dashboard';
  const channelParam = params.get('channel');
  const initialScenario = params.get('scenario');
  const [channels, setChannels] = useState<ComedyChannel[]>([]);
  const [channelId, setChannelId] = useState<string | null>(channelParam);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loadChannels = async () => {
    setLoading(true); setError(null);
    try {
      const response = await api.comedy.channels();
      setChannels(response.channels);
      setChannelId((current) => {
        if (current && response.channels.some((channel) => channel.id === current)) return current;
        return response.channels[0]?.id ?? null;
      });
    } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void loadChannels(); }, []);

  const go = (nextTab: ComedyTabId, nextChannelId: string | null = channelId) => {
    const query = new URLSearchParams();
    query.set('section', nextTab);
    if (nextChannelId) query.set('channel', nextChannelId);
    navigate(`/comedy?${query.toString()}`);
  };

  const selectChannel = (id: string) => {
    setChannelId(id);
    go(tab === 'dashboard' || tab === 'channels' || tab === 'settings' || tab === 'help' ? 'dashboard' : tab, id);
  };

  const channel = channels.find((entry) => entry.id === channelId) ?? null;

  if (loading) return <div className="comedy-page"><Spinner label="Ładowanie Comedy Studio…" /></div>;
  if (error) return <div className="comedy-page"><ErrorAlert error={error} /><Button onClick={() => void loadChannels()}>Spróbuj ponownie</Button></div>;

  return (
    <div className="comedy-page">
      <PageHeader
        title="AI Comedy Mashup Studio"
        subtitle="Wielokanałowa produkcja komediowych Shorts/Reels: research, biblioteka realnych wypowiedzi, scenariusze, render, QA i publikacja."
        actions={<Button size="sm" onClick={() => void loadChannels()}>Odśwież kanały</Button>}
      />

      <div className="comedy-tabs" role="tablist" aria-label="Comedy Studio">
        {COMEDY_TABS.map((item) => (
          <Button key={item.id} size="sm" role="tab" aria-selected={tab === item.id} onClick={() => go(item.id)}>
            {item.label}
          </Button>
        ))}
      </div>

      {!['dashboard', 'channels', 'settings', 'help'].includes(tab) && channels.length > 0 && (
        <div className="comedy-channel-bar">
          <label className="field" style={{ marginBottom: 0, minWidth: 260 }}>
            <span className="field__label">Kanał tematyczny</span>
            <select value={channelId ?? ''} onChange={(event) => selectChannel(event.target.value)}>
              {channels.filter((entry) => entry.status !== 'ARCHIVED').map((entry) => (
                <option key={entry.id} value={entry.id}>{entry.name} · {entry.language.toUpperCase()} · {entry.automationMode}</option>
              ))}
            </select>
          </label>
          {channel && (
            <div className="muted" style={{ fontSize: 12, paddingBottom: 6 }}>
              {channel.postsPerDay} filmów/dzień · {channel.durationMinSeconds}–{channel.durationMaxSeconds} s · napisy {channel.captionStyle} · budżet AI ${channel.aiBudgetDailyUsd.toFixed(2)}/dzień
            </div>
          )}
        </div>
      )}

      {tab === 'dashboard' && (
        <ComedyDashboardPanel onOpenChannel={(id) => selectChannel(id)} onOpenTab={(next) => go(next as ComedyTabId)} />
      )}

      {tab === 'channels' && (
        <ComedyChannelsPanel channels={channels} selectedId={channelId} onSelect={(id) => selectChannel(id)} onRefresh={() => void loadChannels()} />
      )}

      {tab === 'settings' && <ComedySettingsPanel onChanged={() => void loadChannels()} />}

      {tab === 'help' && <ComedyHelpPanel />}

      {!['dashboard', 'channels', 'settings', 'help'].includes(tab) && !channel && (
        <ComedyEmpty
          title="Najpierw utwórz kanał tematyczny"
          hint="Research, scenariusze i publikacje są zawsze przypisane do konkretnego kanału. Utwórz kanał, aby kontynuować."
          action={<Button variant="primary" onClick={() => go('channels')}>Przejdź do kanałów</Button>}
        />
      )}

      {channel && tab === 'research' && <ComedyResearchPanel channel={channel} />}
      {channel && tab === 'sources' && <ComedySourcesPanel channel={channel} />}
      {channel && tab === 'library' && <ComedyLibraryPanel channel={channel} />}
      {channel && tab === 'scenarios' && <ComedyScenariosPanel channel={channel} onOpenDirector={(scenarioId) => { const query = new URLSearchParams({ section: 'director', channel: channel.id }); if (scenarioId) query.set('scenario', scenarioId); navigate(`/comedy?${query.toString()}`); }} />}
      {channel && tab === 'director' && <ComedyDirectorPanel channel={channel} initialScenarioId={initialScenario} />}
      {channel && tab === 'studio' && <ComedyVideoStudioPanel channel={channel} />}
      {channel && tab === 'queue' && <ComedyQueuePanel channel={channel} />}
      {channel && tab === 'schedule' && <ComedySchedulePanel channel={channel} />}
      {channel && tab === 'publications' && <ComedyPublicationsPanel channel={channel} />}
      {channel && tab === 'analytics' && <ComedyAnalyticsPanel channel={channel} />}
    </div>
  );
}
